import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

/**
 * Spolocny zaklad animovanych ukazok na landing stranke (HeroMockup,
 * UkazkaRestauracia, UkazkaVodic).
 *
 * Ukazka je pribeh rozdeleny na kroky -- `kroky` je pole [nazov, cas v ms od
 * zaciatku kola]. Hook len posuva cislo kroku, vsetok pohyb robia CSS prechody
 * nad triedami, ktore z kroku odvodi komponent. Po `dlzkaKola` sa kolo zacne
 * odznova (`kolo` sa zvysi -- komponent ho da ako `key` obsahu, ktory sa tak
 * vytvori znova v pociatocnom stave).
 *
 * Bezi len ked je ukazka na obrazovke a karta prehliadaca viditelna. Bez
 * IntersectionObserver a matchMedia (jsdom: testy, prerender) a pri
 * prefers-reduced-motion sa sam nepohne -- ostava prvy zaber, no `skocNa`
 * funguje aj tak (staticky prechod medzi zabermi).
 */
export function useScenar(rootRef, kroky, dlzkaKola) {
  const [kolo, setKolo] = useState(0);
  const [krok, setKrok] = useState(0);
  const krokRef = useRef(0);
  const skokRef = useRef(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined" || !window.matchMedia) return undefined;
    const pokoj = window.matchMedia("(prefers-reduced-motion: reduce)");

    let casovace = [];
    let bezi = false;
    let naObrazovke = false;

    const nastav = (i) => {
      krokRef.current = i;
      setKrok(i);
    };
    const zrus = () => {
      casovace.forEach(clearTimeout);
      casovace = [];
    };
    // naplanuje zvysok kola od kroku `od`
    const planujOd = (od) => {
      const t0 = kroky[od][1];
      casovace = kroky.slice(od + 1).map(([, cas], i) => setTimeout(() => nastav(od + 1 + i), cas - t0));
      casovace.push(setTimeout(() => {
        setKolo((k) => k + 1);
        nastav(0);
        planujOd(0);
      }, dlzkaKola - t0));
    };
    const smie = () => naObrazovke && !document.hidden && !pokoj.matches;
    const aktualizuj = () => {
      if (!smie()) {
        zrus();
        bezi = false;
        return;
      }
      if (bezi) return;
      bezi = true;
      // prerusene kolo sa nedohrava -- zacne sa odznova (mimo obrazovky, nik nevidi skok)
      if (krokRef.current !== 0) {
        setKolo((k) => k + 1);
        nastav(0);
      }
      planujOd(0);
    };

    skokRef.current = (i) => {
      zrus();
      setKolo((k) => k + 1);
      nastav(i);
      if (smie()) {
        bezi = true;
        planujOd(i);
      }
    };

    const pozorovatel = new IntersectionObserver(([zaznam]) => {
      naObrazovke = zaznam.isIntersecting;
      aktualizuj();
    }, { threshold: 0.25 });
    pozorovatel.observe(el);
    document.addEventListener("visibilitychange", aktualizuj);
    pokoj.addEventListener?.("change", aktualizuj);

    return () => {
      zrus();
      skokRef.current = null;
      pozorovatel.disconnect();
      document.removeEventListener("visibilitychange", aktualizuj);
      pokoj.removeEventListener?.("change", aktualizuj);
    };
  }, [rootRef, kroky, dlzkaKola]);

  const poradie = useMemo(() => Object.fromEntries(kroky.map(([nazov], i) => [nazov, i])), [kroky]);
  const po = (nazov) => krok >= poradie[nazov];
  // `po` pre iny krok -- na vykreslenie predosleho zaberu pri prelinani
  const poPre = (i) => (nazov) => i >= poradie[nazov];

  // bez behu animacie (jsdom, reduced motion) sa aspon prepne zaber
  const skocNa = useCallback((nazov) => {
    const i = poradie[nazov];
    if (skokRef.current) {
      skokRef.current(i);
    } else {
      krokRef.current = i;
      setKolo((k) => k + 1);
      setKrok(i);
    }
  }, [poradie]);

  return { kolo, krok, po, poPre, skocNa };
}

export const PRELINANIE_MS = 900;

const obmedzenyPohyb = () =>
  typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

/**
 * Plynuly prechod medzi kolami (alebo pri skoku na kapitolu): ked sa zmeni
 * `kolo`, vrati zaber, ktory bol na obrazovke tesne predtym -- { kolo, krok,
 * extra } -- a po PRELINANIE_MS null. Komponent ho vykresli este raz navrch
 * (bez vlastnych animacii) a necha ho rozplynut, takze medzi kolami nie je
 * prazdne okno. `odKroku`: prelinat len ked predosly zaber bol aspon v tomto
 * kroku. `extra`: cokolvek dalsie, co zaber potrebuje (napr. posun zoznamu).
 * Pri prefers-reduced-motion sa neprelina -- zaber sa vymeni naraz.
 */
export function usePrelinanie(kolo, krok, odKroku = 0, extra = null) {
  const predosle = useRef(null);
  const [odchadza, setOdchadza] = useState(null);

  // pred vykreslenim na obrazovku -- inak by novy zaber na snimku bliknul bez prekryvu
  useLayoutEffect(() => {
    const p = predosle.current;
    if (p && kolo !== p.kolo && p.krok >= odKroku && !obmedzenyPohyb()) setOdchadza(p);
    predosle.current = { kolo, krok, extra };
  }, [kolo, krok, odKroku, extra]);

  useEffect(() => {
    if (odchadza === null) return undefined;
    const casovac = setTimeout(() => setOdchadza(null), PRELINANIE_MS);
    return () => clearTimeout(casovac);
  }, [odchadza]);

  return odchadza;
}

/** Cislo, ktore sa pri zmene "dopocita" na novu hodnotu. */
export function Pocitadlo({ hodnota, format }) {
  const [zobrazene, setZobrazene] = useState(hodnota);
  const odRef = useRef(hodnota);

  useEffect(() => {
    const od = odRef.current;
    if (od === hodnota) return undefined;
    const t0 = performance.now();
    let raf;
    const snimka = (t) => {
      const p = Math.min(1, (t - t0) / 700);
      setZobrazene(od + (hodnota - od) * (1 - (1 - p) ** 3));
      if (p < 1) raf = requestAnimationFrame(snimka);
    };
    raf = requestAnimationFrame(snimka);
    return () => {
      cancelAnimationFrame(raf);
      odRef.current = hodnota;
    };
  }, [hodnota]);

  return format(zobrazene);
}

/**
 * Mierka, ktorou sa scena navrhnuta na `sirka` px zmesti do sirky ramu `ref`
 * (nikdy sa nezvacsuje). Bez ResizeObserver (jsdom) ostava 1.
 */
export function useMierka(ref, sirka) {
  const [mierka, setMierka] = useState(1);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const pozorovatel = new ResizeObserver(([zaznam]) => {
      setMierka(Math.min(1, zaznam.contentRect.width / sirka));
    });
    pozorovatel.observe(el);
    return () => pozorovatel.disconnect();
  }, [ref, sirka]);

  return mierka;
}

/**
 * Poloha prvku voci predkovi `koren` v layoutovych px -- cez offsetParent, aby
 * ju neskreslila mierka (transform) sceny. Skryty prvok alebo prvok mimo
 * `koren` vrati null.
 */
export function polohaV(el, koren) {
  if (!el || !koren || !el.offsetParent) return null;
  let x = 0;
  let y = 0;
  let p = el;
  while (p && p !== koren) {
    x += p.offsetLeft;
    y += p.offsetTop;
    p = p.offsetParent;
  }
  return p === koren ? { x, y, w: el.offsetWidth, h: el.offsetHeight } : null;
}

export const eura = (v) => `${v.toFixed(2).replace(".", ",")} €`;
// Prehlad a Dashboard pisu sumy cez toFixed(2) -- s bodkou
export const euraBodka = (v) => `${v.toFixed(2)} €`;
export const kusy = (v) => String(Math.round(v));
